import { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  AppBar,
  Toolbar,
  Autocomplete,
  TextField,
  Checkbox,
  Chip,
  LinearProgress,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  Translate as TranslateIcon,
  ArrowForward as ArrowForwardIcon,
  Description as DescriptionIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  PictureAsPdf as PictureAsPdfIcon,
  TableChart as TableChartIcon,
  Slideshow as SlideshowIcon,
  InsertDriveFile as InsertDriveFileIcon,
} from "@mui/icons-material";
import JSZip from "jszip";
import { translateDocument, previewDocx } from "../../api";
import {
  LanguageCode,
  languageCodeToLabel,
} from "../../pages/playground/types";

const SUPPORTED_EXTENSIONS = [
  // Office formats
  ".docx",
  ".docm",
  ".dotx",
  ".dotm",
  ".xlsx",
  ".xlsm",
  ".xltx",
  ".xltm",
  ".pptx",
  ".pptm",
  ".ppsx",
  ".ppsm",
  ".potx",
  ".potm",
  // OpenDocument formats
  ".odt",
  ".ott",
  ".ods",
  ".ots",
  ".odp",
  ".otp",
  // PDF
  ".pdf",
  // Web/Markup formats
  ".htm",
  ".html",
  ".xhtml",
  ".xml",
  ".json",
  ".yaml",
  ".md",
  ".markdown",
  // Localization formats
  ".xlf",
  ".xliff",
  ".po",
  ".resx",
  ".ttx",
  ".txml",
  ".wix",
  // Publishing formats
  ".icml",
  ".idml",
  ".mif",
  ".dita",
  ".tex",
  // Subtitle formats
  ".srt",
  ".vtt",
  // Data formats
  ".csv",
  ".tsv",
  ".properties",
  ".php",
  ".ts",
  ".txt",
  ".strings",
  ".xini",
];

interface DocumentTranslationProps {
  compact?: boolean;
  onClose?: () => void;
}

// Result tracking for multi-language translations
interface TranslationResult {
  language: LanguageCode;
  status: "pending" | "translating" | "completed" | "error";
  blob?: Blob;
  filename?: string;
  error?: string;
}

export default function DocumentTranslation({
  compact = false,
  onClose,
}: DocumentTranslationProps) {
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);

  // Language selection (source is optional - "auto" means auto-detect)
  const [sourceLanguage, setSourceLanguage] = useState<LanguageCode | "auto">(
    "auto"
  );
  const [targetLanguages, setTargetLanguages] = useState<LanguageCode[]>([]);

  // File handling
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [originalBlobUrl, setOriginalBlobUrl] = useState<string | null>(null);

  // Preview URLs (converted PDFs for DOCX files)
  const [originalPreviewUrl, setOriginalPreviewUrl] = useState<string | null>(
    null
  );
  const [isConvertingOriginal, setIsConvertingOriginal] = useState(false);

  // Translation results for multi-language
  const [translationResults, setTranslationResults] = useState<
    TranslationResult[]
  >([]);
  const [overallProgress, setOverallProgress] = useState(0);

  // UI state
  const [isTranslating, setIsTranslating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const languageOptions = Object.values(LanguageCode);
  const isPdf = selectedFile?.name.toLowerCase().endsWith(".pdf");

  // Track blob URLs for cleanup
  const blobUrlsRef = useRef({
    originalBlobUrl: null as string | null,
    originalPreviewUrl: null as string | null,
  });
  const translationResultsRef = useRef<TranslationResult[]>([]);

  // Keep refs in sync with state
  useEffect(() => {
    blobUrlsRef.current = {
      originalBlobUrl,
      originalPreviewUrl,
    };
  }, [originalBlobUrl, originalPreviewUrl]);

  useEffect(() => {
    translationResultsRef.current = translationResults;
  }, [translationResults]);

  // Cleanup blob URLs on unmount only
  useEffect(() => {
    return () => {
      const urls = blobUrlsRef.current;
      if (urls.originalBlobUrl) URL.revokeObjectURL(urls.originalBlobUrl);
      if (urls.originalPreviewUrl) URL.revokeObjectURL(urls.originalPreviewUrl);
      // Cleanup translation result blobs
      translationResultsRef.current.forEach((result) => {
        if (result.blob) {
          URL.revokeObjectURL(URL.createObjectURL(result.blob));
        }
      });
    };
  }, []);

  const isValidFile = (file: File): boolean => {
    const extension = file.name.toLowerCase().slice(file.name.lastIndexOf("."));
    return SUPPORTED_EXTENSIONS.includes(extension);
  };

  const handleFileSelect = useCallback(
    async (file: File) => {
      if (!isValidFile(file)) {
        setError(
          "Unsupported file format. Supported: PDF, Office, HTML, XML, subtitles, and more."
        );
        return;
      }

      setError(null);
      setSelectedFile(file);

      // Clean up old URLs
      if (originalBlobUrl) URL.revokeObjectURL(originalBlobUrl);
      if (originalPreviewUrl) URL.revokeObjectURL(originalPreviewUrl);

      // Create blob URL for original file (for download)
      const url = URL.createObjectURL(file);
      setOriginalBlobUrl(url);
      setOriginalPreviewUrl(null);
      setTranslationResults([]);
      setOverallProgress(0);

      // If DOCX, convert to PDF for preview (only for previewable formats)
      const ext = file.name.toLowerCase().slice(file.name.lastIndexOf("."));
      if (ext === ".docx") {
        setIsConvertingOriginal(true);
        try {
          const pdfBlob = await previewDocx(file);
          const previewUrl = URL.createObjectURL(pdfBlob);
          setOriginalPreviewUrl(previewUrl);
        } catch (err) {
          console.error("Preview conversion failed:", err);
          // Silently fail - will show file card instead
        } finally {
          setIsConvertingOriginal(false);
        }
      }
    },
    [originalBlobUrl, originalPreviewUrl]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        handleFileSelect(files[0]);
      }
    },
    [handleFileSelect]
  );

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleTranslate = async () => {
    if (!selectedFile || targetLanguages.length === 0) return;

    setIsTranslating(true);
    setError(null);
    setOverallProgress(0);

    // Initialize results for all languages
    const initialResults: TranslationResult[] = targetLanguages.map((lang) => ({
      language: lang,
      status: "pending" as const,
    }));
    setTranslationResults(initialResults);

    const ext = selectedFile.name.substring(selectedFile.name.lastIndexOf("."));
    const baseName = selectedFile.name.substring(
      0,
      selectedFile.name.lastIndexOf(".")
    );

    // Function to translate a single language
    const translateOne = async (
      lang: LanguageCode,
      index: number
    ): Promise<void> => {
      // Mark as translating
      setTranslationResults((prev) =>
        prev.map((r, i) =>
          i === index ? { ...r, status: "translating" as const } : r
        )
      );

      try {
        const blob = await translateDocument({
          file: selectedFile,
          source: sourceLanguage === "auto" ? undefined : sourceLanguage,
          target: lang,
          style: "faithful",
        });

        const filename = `translated_${baseName}_${lang}${ext}`;

        // Mark as completed with blob
        setTranslationResults((prev) =>
          prev.map((r, i) =>
            i === index
              ? { ...r, status: "completed" as const, blob, filename }
              : r
          )
        );
      } catch (err) {
        console.error(`Translation failed for ${lang}:`, err);
        // Mark as error
        setTranslationResults((prev) =>
          prev.map((r, i) =>
            i === index
              ? {
                  ...r,
                  status: "error" as const,
                  error:
                    err instanceof Error ? err.message : "Translation failed",
                }
              : r
          )
        );
      }
    };

    // Execute with concurrency limit of 3
    const CONCURRENCY_LIMIT = 3;
    let completedCount = 0;

    for (let i = 0; i < targetLanguages.length; i += CONCURRENCY_LIMIT) {
      const chunk = targetLanguages.slice(i, i + CONCURRENCY_LIMIT);
      await Promise.allSettled(
        chunk.map((lang, chunkIndex) => {
          const globalIndex = i + chunkIndex;
          return translateOne(lang, globalIndex);
        })
      );
      completedCount += chunk.length;
      setOverallProgress(
        Math.round((completedCount / targetLanguages.length) * 100)
      );
    }

    setIsTranslating(false);
  };

  const handleDownload = (url: string, filename: string) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    // Small delay before removing to ensure download starts
    setTimeout(() => document.body.removeChild(a), 100);
  };

  const handleClearFile = () => {
    if (originalBlobUrl) URL.revokeObjectURL(originalBlobUrl);
    if (originalPreviewUrl) URL.revokeObjectURL(originalPreviewUrl);
    setSelectedFile(null);
    setOriginalBlobUrl(null);
    setOriginalPreviewUrl(null);
    setTranslationResults([]);
    setOverallProgress(0);
    setError(null);
  };

  // Download all translations as ZIP or single file
  const handleDownloadAll = async () => {
    const successfulResults = translationResults.filter(
      (r) => r.status === "completed" && r.blob && r.filename
    );

    if (successfulResults.length === 0) return;

    if (successfulResults.length === 1) {
      // Single file - download directly
      const result = successfulResults[0];
      const url = URL.createObjectURL(result.blob!);
      handleDownload(url, result.filename!);
      URL.revokeObjectURL(url);
      return;
    }

    // Multiple files - create zip
    const zip = new JSZip();
    for (const result of successfulResults) {
      zip.file(result.filename!, result.blob!);
    }

    const zipBlob = await zip.generateAsync({ type: "blob" });
    const baseName = selectedFile!.name.substring(
      0,
      selectedFile!.name.lastIndexOf(".")
    );
    const url = URL.createObjectURL(zipBlob);
    handleDownload(url, `translated_${baseName}.zip`);
    URL.revokeObjectURL(url);
  };

  // Download a single translation result
  const handleDownloadSingle = (result: TranslationResult) => {
    if (!result.blob || !result.filename) return;
    const url = URL.createObjectURL(result.blob);
    handleDownload(url, result.filename);
    URL.revokeObjectURL(url);
  };

  const handleCloseDialog = () => {
    // Check if any translations were completed before closing
    const hadTranslations = translationResults.some(r => r.status === 'completed');
    handleClearFile();
    setDialogOpen(false);
    // Notify parent to refresh usage if translations were done
    if (hadTranslations && onClose) {
      onClose();
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Get format-specific icon for file
  const getFileIcon = (filename: string) => {
    const ext = filename.toLowerCase().slice(filename.lastIndexOf("."));
    if (ext === ".pdf")
      return <PictureAsPdfIcon sx={{ fontSize: 64, color: "error.main" }} />;
    if (
      [".docx", ".doc", ".docm", ".dotx", ".dotm", ".odt", ".ott"].includes(ext)
    )
      return <DescriptionIcon sx={{ fontSize: 64, color: "primary.main" }} />;
    if (
      [
        ".xlsx",
        ".xls",
        ".xlsm",
        ".xltx",
        ".xltm",
        ".ods",
        ".ots",
        ".csv",
        ".tsv",
      ].includes(ext)
    )
      return <TableChartIcon sx={{ fontSize: 64, color: "success.main" }} />;
    if (
      [
        ".pptx",
        ".ppt",
        ".pptm",
        ".ppsx",
        ".ppsm",
        ".potx",
        ".potm",
        ".odp",
        ".otp",
      ].includes(ext)
    )
      return <SlideshowIcon sx={{ fontSize: 64, color: "warning.main" }} />;
    return <InsertDriveFileIcon sx={{ fontSize: 64, color: "grey.500" }} />;
  };

  // File card component for non-previewable files
  const FileCard = ({
    filename,
    fileSize,
    onDownload,
  }: {
    filename: string;
    fileSize?: number;
    onDownload: () => void;
  }) => (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "grey.50",
      }}
    >
      <Paper
        elevation={2}
        sx={{
          p: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
          maxWidth: 300,
        }}
      >
        {getFileIcon(filename)}
        <Typography
          variant="body1"
          fontWeight="medium"
          textAlign="center"
          noWrap
          sx={{ maxWidth: 250 }}
        >
          {filename}
        </Typography>
        {fileSize && (
          <Typography variant="body2" color="text.secondary">
            {formatFileSize(fileSize)}
          </Typography>
        )}
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={onDownload}
        >
          Download
        </Button>
      </Paper>
    </Box>
  );

  return (
    <>
      {/* Trigger Button */}
      {compact ? (
        <Button
          variant="outlined"
          startIcon={<TranslateIcon />}
          onClick={() => setDialogOpen(true)}
        >
          Translate Document
        </Button>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
          }}
        >
          <TranslateIcon sx={{ fontSize: 64, color: "primary.main", mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Document Translation
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 3, textAlign: "center", maxWidth: 400 }}
          >
            Translate documents using Lara AI translation. Supports PDF, Office,
            HTML, XML, subtitles, and 40+ file formats.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<TranslateIcon />}
            onClick={() => setDialogOpen(true)}
          >
            Open Translator
          </Button>
        </Box>
      )}

      {/* Fullscreen Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        fullScreen
        PaperProps={{
          sx: { display: "flex", flexDirection: "column" },
        }}
      >
        {/* Fixed App Bar */}
        <AppBar position="static" color="default" elevation={1}>
          <Toolbar sx={{ gap: 2 }}>
            <IconButton edge="start" onClick={handleCloseDialog}>
              <CloseIcon />
            </IconButton>

            <Typography variant="h6" sx={{ mr: 2 }}>
              Translate Document
            </Typography>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="source-language-label">Source</InputLabel>
              <Select
                labelId="source-language-label"
                value={sourceLanguage}
                label="Source"
                onChange={(e) =>
                  setSourceLanguage(e.target.value as LanguageCode | "auto")
                }
              >
                <MenuItem value="auto">
                  <em>Auto-detect</em>
                </MenuItem>
                {languageOptions.map((lang) => (
                  <MenuItem key={lang} value={lang}>
                    {languageCodeToLabel(lang)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <ArrowForwardIcon sx={{ color: "text.secondary" }} />

            <Autocomplete
              multiple
              size="small"
              options={languageOptions}
              value={targetLanguages}
              onChange={(_, newValue) => setTargetLanguages(newValue)}
              getOptionLabel={(option) => languageCodeToLabel(option)}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    checked={selected}
                    sx={{ mr: 1 }}
                  />
                  {languageCodeToLabel(option)}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Target Languages"
                  placeholder="Select languages"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option}
                    label={languageCodeToLabel(option)}
                    size="small"
                  />
                ))
              }
              sx={{ minWidth: 300 }}
              disableCloseOnSelect
            />

            <Box sx={{ flexGrow: 1 }} />

            <Button
              variant="contained"
              onClick={handleTranslate}
              disabled={
                !selectedFile || targetLanguages.length === 0 || isTranslating
              }
              startIcon={
                isTranslating ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <TranslateIcon />
                )
              }
            >
              {isTranslating
                ? "Translating..."
                : `Translate${
                    targetLanguages.length > 1
                      ? ` (${targetLanguages.length})`
                      : ""
                  }`}
            </Button>
          </Toolbar>
        </AppBar>

        {/* Error Display */}
        {error && (
          <Alert
            severity="error"
            sx={{ m: 1, flexShrink: 0 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Main Content - Two Column Split */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 1,
            p: 1,
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          {/* Left Column: Original */}
          <Paper
            variant="outlined"
            sx={{
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              minHeight: 0,
            }}
          >
            <Box
              sx={{
                p: 1,
                borderBottom: 1,
                borderColor: "divider",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                bgcolor: "grey.100",
                flexShrink: 0,
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold">
                Original
              </Typography>
              {selectedFile && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ maxWidth: 120 }}
                  >
                    {selectedFile.name}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() =>
                      handleDownload(originalBlobUrl!, selectedFile.name)
                    }
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={handleClearFile}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                flex: 1,
                overflow: "hidden",
                position: "relative",
                minHeight: 0,
              }}
            >
              {!selectedFile ? (
                /* Upload area */
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 3,
                    cursor: "pointer",
                    backgroundColor: isDragging ? "action.hover" : "grey.50",
                    transition: "all 0.2s ease",
                    border: isDragging
                      ? "2px dashed"
                      : "2px dashed transparent",
                    borderColor: isDragging ? "primary.main" : "transparent",
                  }}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onClick={() => document.getElementById("file-input")?.click()}
                >
                  <input
                    id="file-input"
                    type="file"
                    accept={SUPPORTED_EXTENSIONS.join(",")}
                    style={{ display: "none" }}
                    onChange={handleFileInputChange}
                  />
                  <CloudUploadIcon
                    sx={{ fontSize: 56, color: "text.disabled", mb: 2 }}
                  />
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    gutterBottom
                  >
                    Drop your file here
                  </Typography>
                  <Typography variant="body2" color="text.disabled">
                    PDF, Office, HTML, XML, subtitles, and more
                  </Typography>
                </Box>
              ) : isConvertingOriginal ? (
                /* Converting DOCX to PDF for preview */
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "grey.50",
                  }}
                >
                  <CircularProgress sx={{ mb: 2 }} />
                  <Typography color="text.secondary">
                    Preparing preview...
                  </Typography>
                </Box>
              ) : isPdf ? (
                /* PDF preview (native PDF file) */
                <Box
                  component="iframe"
                  src={`${originalBlobUrl!}#navpanes=0&scrollbar=1`}
                  sx={{ width: "100%", height: "100%", border: "none" }}
                />
              ) : originalPreviewUrl ? (
                /* DOCX preview (converted to PDF) */
                <Box
                  component="iframe"
                  src={`${originalPreviewUrl}#navpanes=0&scrollbar=1`}
                  sx={{ width: "100%", height: "100%", border: "none" }}
                />
              ) : (
                /* Non-previewable files or DOCX conversion failed - show file card */
                <FileCard
                  filename={selectedFile.name}
                  fileSize={selectedFile.size}
                  onDownload={() =>
                    handleDownload(originalBlobUrl!, selectedFile.name)
                  }
                />
              )}
            </Box>
          </Paper>

          {/* Right Column: Translations */}
          <Paper
            variant="outlined"
            sx={{
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              minHeight: 0,
            }}
          >
            <Box
              sx={{
                p: 1,
                borderBottom: 1,
                borderColor: "divider",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                bgcolor: "grey.100",
                flexShrink: 0,
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold">
                Translations
              </Typography>
              {translationResults.some((r) => r.status === "completed") && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadAll}
                >
                  {translationResults.filter((r) => r.status === "completed")
                    .length > 1
                    ? "Download All (ZIP)"
                    : "Download"}
                </Button>
              )}
            </Box>

            <Box
              sx={{
                flex: 1,
                overflow: "auto",
                position: "relative",
                minHeight: 0,
                p: 2,
              }}
            >
              {isTranslating ? (
                /* Translation in progress */
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography variant="body2">
                      Translating:{" "}
                      {
                        translationResults.filter(
                          (r) => r.status === "completed"
                        ).length
                      }{" "}
                      / {translationResults.length}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={overallProgress}
                  />
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}
                  >
                    {translationResults.map((result) => (
                      <Chip
                        key={result.language}
                        label={languageCodeToLabel(result.language)}
                        size="small"
                        color={
                          result.status === "completed"
                            ? "success"
                            : result.status === "error"
                            ? "error"
                            : result.status === "translating"
                            ? "secondary"
                            : "default"
                        }
                        variant={
                          result.status === "pending" ? "outlined" : "filled"
                        }
                        icon={
                          result.status === "translating" ? (
                            <CircularProgress size={12} color="inherit" />
                          ) : undefined
                        }
                      />
                    ))}
                  </Box>
                </Box>
              ) : translationResults.length === 0 ? (
                /* No translations yet */
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "text.disabled",
                  }}
                >
                  <TranslateIcon sx={{ fontSize: 56, mb: 2, opacity: 0.3 }} />
                  <Typography>
                    Select target languages and click Translate
                  </Typography>
                </Box>
              ) : (
                /* Translation results */
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {translationResults.some((r) => r.status === "error") && (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      Some translations failed. Successfully completed:{" "}
                      {
                        translationResults.filter(
                          (r) => r.status === "completed"
                        ).length
                      }{" "}
                      / {translationResults.length}
                    </Alert>
                  )}
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {translationResults.map((result) => (
                      <Chip
                        key={result.language}
                        label={languageCodeToLabel(result.language)}
                        color={
                          result.status === "completed" ? "success" : "error"
                        }
                        variant="filled"
                        onDelete={
                          result.status === "completed"
                            ? () => handleDownloadSingle(result)
                            : undefined
                        }
                        deleteIcon={
                          result.status === "completed" ? (
                            <DownloadIcon />
                          ) : undefined
                        }
                        title={
                          result.status === "error"
                            ? result.error
                            : result.filename
                        }
                      />
                    ))}
                  </Box>
                  {translationResults.some((r) => r.status === "completed") && (
                    <Typography variant="caption" color="text.secondary">
                      Click the download icon on each chip to download
                      individual files, or use "Download All" for a ZIP archive.
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      </Dialog>
    </>
  );
}
